// Merkavah Mysticism Frameworks and Data

export interface Creature {
 name: string;
 hebrewName: string;
 face: string;
 direction: string;
 element: string;
 attribute: string;
 description: string;
}

export interface Heaven {
 number: number;
 name: string;
 hebrewName: string;
 ruler: string;
 description: string;
 challenges: string[];
 rewards: string[];
}

export interface Sefira {
 name: string;
 hebrewName: string;
 meaning: string;
 position: { x: number; y: number };
 color: string;
 attribute: string;
}

// The Four Living Creatures (Chayot HaKodesh)
export const chayot: Creature[] = [
 {
 name:'Lion',
 hebrewName:'אריה',
 face:'Right',
 direction:'South',
 element:'Fire',
 attribute:'Chesed (Loving-kindness)',
 description:'The lion represents the quality of bold, expansive love. Its roar echoes the power of divine grace spreading outward.'
 },
 {
 name:'Ox',
 hebrewName:'שור',
 face:'Left',
 direction:'North',
 element:'Earth',
 attribute:'Gevurah (Strength/Judgment)',
 description:'The ox embodies strength and restraint. It represents the power of discipline and divine judgment.'
 },
 {
 name:'Eagle',
 hebrewName:'נשר',
 face:'Back',
 direction:'West',
 element:'Air',
 attribute:'Tiferet (Beauty/Harmony)',
 description:'The eagle soars above, representing the harmonizing principle that balances love and judgment.'
 },
 {
 name:'Human',
 hebrewName:'אדם',
 face:'Front',
 direction:'East',
 element:'Water',
 attribute:'Malkhut (Kingdom/Sovereignty)',
 description:'The human face represents consciousness and the capacity to receive and actualize divine will on earth.'
 }
];

// The Seven Heavens (Sheva Reki\'im)
export const sevenHeavens: Heaven[] = [
 {
 number: 1,
 name:'Vilon',
 hebrewName:'וילון',
 ruler:'Gabriel',
 description:'The Curtain - Creates the separation between day and night. The first veil between physical and spiritual realms.',
 challenges: ['Releasing attachment to physical perceptions','Accepting that reality extends beyond the visible'],
 rewards: ['Initial glimpse of celestial light','Connection to protective angelic presence']
 },
 {
 number: 2,
 name:'Rakia',
 hebrewName:'רקיע',
 ruler:'Zachriel',
 description:'The Firmament - Where the stars, sun, and moon are fixed. Contains the celestial bodies that mark sacred time.',
 challenges: ['Understanding cosmic patterns','Aligning personal rhythms with divine cycles'],
 rewards: ['Wisdom of sacred timing','Attunement to celestial influences']
 },
 {
 number: 3,
 name:'Shchakim',
 hebrewName:'שחקים',
 ruler:'Anahel',
 description:'The Clouds - Where the millstones grind manna for the righteous. Source of spiritual sustenance.',
 challenges: ['Trusting in divine provision','Developing spiritual hunger'],
 rewards: ['Sustenance for the soul','Taste of heavenly nourishment']
 },
 {
 number: 4,
 name:'Zebul',
 hebrewName:'זבול',
 ruler:'Michael',
 description:'The Habitation - Location of the heavenly Jerusalem and Temple. Center of celestial worship.',
 challenges: ['Purification of intention','Alignment with sacred purpose'],
 rewards: ['Access to the Supernal Temple','Experience of divine service']
 },
 {
 number: 5,
 name:'Ma\'on',
 hebrewName:'מעון',
 ruler:'Samael',
 description:'The Dwelling - Where bands of ministering angels sing by night. Realm of continuous praise.',
 challenges: ['Developing capacity for praise','Overcoming spiritual aridity'],
 rewards: ['Joining the angelic choir','Experiencing divine joy']
 },
 {
 number: 6,
 name:'Machon',
 hebrewName:'מכון',
 ruler:'Sandalphon',
 description:'The Foundation - Storehouses of snow, hail, and dew. Treasury of divine powers for world transformation.',
 challenges: ['Understanding divine providence','Accepting cycles of blessing and challenge'],
 rewards: ['Access to divine treasuries','Power for world-healing']
 },
 {
 number: 7,
 name:'Aravot',
 hebrewName:'ערבות',
 ruler:'Metatron',
 description:'The Highest Heaven - Seat of the Divine Throne. Abode of righteousness, justice, and mercy.',
 challenges: ['Ultimate surrender of ego','Complete alignment with divine will'],
 rewards: ['Vision of the Throne of Glory','Union with the Divine Presence']
 }
];

// Chariot Components
export const chariotComponents = {
 throne: {
 name:'Kiseh HaKavod',
 hebrewName:'כסא הכבוד',
 description:'The Throne of Glory upon which the Divine Presence rests. Made of sapphire, symbolizing the infinite depth of divine wisdom.',
 significance:'The ultimate destination of the mystical ascent, representing the direct experience of divine presence.'
 },
 wheels: {
 name:'Ophanim',
 hebrewName:'אופנים',
 description:'The wheels of the chariot, covered with eyes, representing omniscient awareness and the interconnection of all existence.',
 significance:'Symbolize the dynamic movement of divine energy through creation and the all-seeing nature of providence.'
 },
 creatures: {
 name:'Chayot HaKodesh',
 hebrewName:'חיות הקודש',
 description:'The four holy living creatures that bear the throne, each with four faces and four wings.',
 significance:'Represent the four fundamental forces/aspects of creation and divine manifestation.'
 },
 firmament: {
 name:'Rakia',
 hebrewName:'רקיע',
 description:'The awesome firmament of crystal above the heads of the Chayot, separating the creatures from the throne.',
 significance:'Represents the interface between created beings and the utterly transcendent divine.'
 },
 fire: {
 name:'Esh Mitlakahat',
 hebrewName:'אש מתלקחת',
 description:'The flashing fire that moves between the Chayot, like burning coals and torches.',
 significance:'Divine energy in active transmission, the living force that animates all of creation.'
 }
};

// Key Biblical References
export const biblicalReferences = [
 {
 book:'Ezekiel',
 chapter: 1,
 verses:'1-28',
 title:'The Vision at the River Chebar',
 summary:'Ezekiel\'s primary vision of the divine chariot during his exile in Babylon.'
 },
 {
 book:'Ezekiel',
 chapter: 10,
 verses:'1-22',
 title:'The Chariot in Jerusalem',
 summary:'Second vision showing the chariot departing from the Temple.'
 },
 {
 book:'Isaiah',
 chapter: 6,
 verses:'1-8',
 title:'Vision of the Seraphim',
 summary:'Isaiah\'s throne vision with six-winged angels and the trisagion.'
 },
 {
 book:'1 Kings',
 chapter: 22,
 verses:'19',
 title:'Micaiah\'s Vision',
 summary:'Vision of the Lord on His throne with heavenly host.'
 },
 {
 book:'Daniel',
 chapter: 7,
 verses:'9-10',
 title:'The Ancient of Days',
 summary:'Vision of thrones, the Ancient of Days, and the divine court.'
 }
];

// Hekhalot (Palaces) Literature Key Texts
export const hekhalotTexts = [
 {
 name:'Hekhalot Rabbati',
 meaning:'The Greater Palaces',
 period:'3rd-6th century CE',
 description:'Describes the journey through seven heavenly palaces to reach the divine throne.'
 },
 {
 name:'Hekhalot Zutarti',
 meaning:'The Lesser Palaces',
 period:'3rd-6th century CE',
 description:'Shorter treatise on throne mysticism with practical instructions.'
 },
 {
 name:'Shi\'ur Qomah',
 meaning:'The Measure of the Body',
 period:'3rd-6th century CE',
 description:'Describes the cosmic dimensions of the divine form.'
 },
 {
 name:'Ma\'aseh Merkavah',
 meaning:'The Work of the Chariot',
 period:'3rd-6th century CE',
 description:'Hymns and instructions for the heavenly ascent.'
 },
 {
 name:'Sefer Yetzirah',
 meaning:'Book of Formation',
 period:'2nd-6th century CE',
 description:'Cosmological text describing creation through letters and numbers.'
 }
];

// Practical Meditation Stages
export const meditationStages = [
 {
 stage: 1,
 name:'Hitbonenut',
 meaning:'Contemplation',
 description:'Deep reflection on divine attributes and the structure of creation.',
 practice:'Study of sacred texts, particularly Ezekiel 1 and related passages.'
 },
 {
 stage: 2,
 name:'Hitbodedut',
 meaning:'Seclusion',
 description:'Withdrawal from sensory distractions to focus inward.',
 practice:'Finding a quiet space, closing eyes, regulating breath.'
 },
 {
 stage: 3,
 name:'Visualization',
 meaning:'Internal Seeing',
 description:'Building the inner vision of the chariot and its components.',
 practice:'Systematic visualization of each element: creatures, wheels, firmament, throne.'
 },
 {
 stage: 4,
 name:'Ascent',
 meaning:'Aliyah',
 description:'The experience of rising through successive heavenly levels.',
 practice:'Maintaining visualization while experiencing lightness and elevation.'
 },
 {
 stage: 5,
 name:'Encounter',
 meaning:'Hashgachah',
 description:'Direct experience of divine presence and illumination.',
 practice:'Receptive stillness, allowing awareness to be transformed.'
 },
 {
 stage: 6,
 name:'Return',
 meaning:'Yeridah',
 description:'Careful descent back to ordinary consciousness.',
 practice:'Gradual return, integration of insights, grounding in body.'
 }
];

// Color Symbolism in Merkavah Tradition
export const colorSymbolism = {
 sapphire: {
 hebrew:'ספיר',
 meaning:'Divine throne, heavenly wisdom, infinite depth',
 usage:'The throne is described as having the appearance of sapphire stone'
 },
 amber: {
 hebrew:'חשמל',
 meaning:'The glowing substance around the divine form, electrum',
 usage:'Hashmal - the mysterious radiance surrounding the divine presence'
 },
 fire: {
 hebrew:'אש',
 meaning:'Divine energy, purification, transformation',
 usage:'Fire between the creatures, burning coals, divine radiance'
 },
 crystal: {
 hebrew:'קרח',
 meaning:'Purity, transparency, the interface with transcendence',
 usage:'The awesome firmament of crystal above the creatures\' heads'
 },
 rainbow: {
 hebrew:'קשת',
 meaning:'Covenant, divine mercy, spectrum of attributes',
 usage:'The appearance around the throne like a rainbow in the clouds'
 }
};
